import { Alert } from '@mui/material'
import { useDraftPolicyConfigurations } from './BenefitPolicyService'

/**
 * Tell the operator when the contract they picked already has an unpublished draft.
 *
 * A warning, never a block. Two configurations on one contract is the multi-variant case this
 * project exists to enable, so a second draft is often deliberate — but starting one by accident,
 * and then wondering why the first never appeared, is the easier mistake to make. This says what
 * already exists and leaves the decision alone.
 */
interface ExistingDraftNoticeProps {
  /**  Whether the surrounding dialog is open. The notice goes quiet the moment it is not. */
  open: boolean
  contractId: number | undefined
}

export const ExistingDraftNotice = ({ open, contractId }: ExistingDraftNoticeProps) => {
  const enabled = open && contractId != null
  const { data } = useDraftPolicyConfigurations(enabled)

  const existing = (data?.policies ?? []).filter(row => row.contract === contractId)
  if (!enabled || existing.length === 0) return null

  const periods = existing
    .map(row => row.effective_start ?? 'no start date')
    .slice(0, 3)
    .join(', ')

  return (
    <Alert severity="warning" data-testid="existing-draft-notice">
      This contract already has {existing.length} unpublished{' '}
      {existing.length === 1 ? 'draft' : 'drafts'} ({periods}
      {existing.length > 3 ? ', …' : ''}). Carry on if this is a second plan variant — otherwise
      finish the existing one instead.
    </Alert>
  )
}
